import {
  montosEnLinea,
  limpiarConcepto,
  round2,
  fechaDesdePartes,
  parsearMonto,
  crearFiltroPagina,
  armarDetalle,
} from "./helpers";

/**
 * Plantilla de SANTANDER (Banco Santander México).
 *
 * Formato del "Detalle de movimientos cuenta de cheques":
 *   FECHA | FOLIO | DESCRIPCION | DEPOSITO | RETIRO | SALDO
 *
 * Retos propios de Santander (validados contra la salida real de pdf.js):
 *  - pdf.js emite fecha + importe + saldo en UNA sola línea. En varias filas la
 *    extracción trae una copia "sombra" y TODO queda DUPLICADO en esa misma línea:
 *    "... 49,068.00 49,068.00 3,305,186.44 3,305,186.44" (importe, importe, saldo,
 *    saldo); otras filas vienen sencillas "... 3,480.00 3,308,666.44". Por eso el
 *    importe es el PRIMER monto del renglón y el saldo el ÚLTIMO: así funciona igual
 *    con [importe, saldo] y con [imp, imp, sal, sal]. Las líneas de detalle (RECIBIDO
 *    DE, CLAVE DE RASTREO…) no traen montos y se ignoran.
 *  - Segunda subcuenta "Inversión Creciente" (sin movimientos) y sección fiscal con
 *    UUID/sellos: se acota a la cuenta de cheques para no contaminar la cadena.
 *  - Fecha "DD-MMM-AAAA" (ej. "04-MAY-2026"): trae el año, se usa directo.
 *  - El símbolo de moneda no siempre es "$" (glifo especial pegado al número): la
 *    semilla de saldo se lee tolerando cualquier carácter previo ([^$\d]*).
 *  - Ojo, Santander llama "ABONO" a lo que ENTRA: el tipo se deduce por la variación
 *    del saldo (sube = depósito/cargo, baja = retiro/abono), no por la palabra.
 *    (En el ERP cargo = depósito que entra, abono = pago que sale.)
 */

// Fecha al inicio del renglón: "04-MAY-2026".
const RE_FECHA_INICIO = /^(\d{1,2})-([A-Za-zÁÉÍÓÚáéíóú]{3})-(\d{4})\b/;
// Inicio y fin de la sección de la cuenta de cheques.
const RE_INICIO = /detalle de movimientos cuenta de cheques/i;
const RE_FIN = /^\s*total\b|saldo\s+final\s+del\s+periodo\s*:|detalles de movimientos dinero creciente|informaci[oó]n fiscal/i;
// Saldo del periodo anterior, arranca la cadena de saldos. [^$\d]* salta el símbolo
// de moneda (que a veces es un glifo especial, no "$") pegado al número.
const RE_SALDO_ANTERIOR = /saldo\s+final\s+del\s+periodo\s+anterior\b[^$\d]*\$?\s*(-?\d{1,3}(?:,\d{3})*\.\d{2})/i;
// Primer importe del renglón, para cortar ahí el concepto.
const RE_PRIMER_MONTO = /-?\(?\$?\s?\d{1,3}(?:,\d{3})*\.\d{2}/;

const santander = {
  id: "santander",
  nombre: "Santander",
  clavesSat: ["014"],
  // Detección por identidad PROPIA de Santander. NO uso "santander" a secas porque
  // aparece como banco emisor de un SPEI ("INSTITUCIÓN EMISORA:SANTANDER") en
  // estados de OTROS bancos.
  detectar: (texto) =>
    /banco santander m[eé]xico|grupo financiero santander|bsm970519du8/i.test(texto),
  // Encabezado: "PERIODO DEL 01-MAY-2026 AL 31-MAY-2026" + "SALDO FINAL DEL PERIODO:".
  extraerEncabezado(lineas) {
    const t = (lineas || []).join("\n");
    const per = t.match(
      /periodo\s+del\s+(\d{1,2})-([a-záéíóú]{3})-(\d{4})\s+al\s+(\d{1,2})-([a-záéíóú]{3})-(\d{4})/i
    );
    // La cuenta de cheques es el primer "SALDO FINAL DEL PERIODO:" (la inversión es el segundo).
    const sf = t.match(/saldo\s+final\s+del\s+periodo\s*:[^$\d]*\$?\s*(-?\d{1,3}(?:,\d{3})*\.\d{2})/i);
    return {
      fechaInicial: per ? fechaDesdePartes(per[1], per[2], per[3]) : null,
      fechaFinal: per ? fechaDesdePartes(per[4], per[5], per[6]) : null,
      saldoFinal: sf ? parsearMonto(sf[1]) : null,
    };
  },
  parsear(lineas, anioRef) {
    const arr = lineas || [];

    // Acotar a la cuenta de cheques (de "Detalle de movimientos cuenta de cheques"
    // hasta "TOTAL" / "SALDO FINAL DEL PERIODO:" / la subcuenta de inversión / la
    // sección fiscal).
    let ini = arr.findIndex((l) => RE_INICIO.test(l));
    let fin = arr.length;
    for (let i = ini + 1; i < arr.length; i++) {
      if (RE_FIN.test(arr[i])) { fin = i; break; }
    }
    const seccion = arr.slice(ini + 1, fin);

    // Saldo del periodo anterior para arrancar la cadena de saldos.
    let prevSaldo = null;
    for (const l of arr) {
      const m = l.match(RE_SALDO_ANTERIOR);
      if (m) { prevSaldo = parsearMonto(m[1]); break; }
    }

    // Arma bloques: un movimiento va de su línea-fecha hasta la línea que trae
    // importe + saldo. Una nueva línea-fecha sin importes reinicia el bloque (así
    // la descripción DUPLICADA de los SPEI no genera movimientos dobles).
    // Ya cerrado un movimiento, las líneas que siguen hasta la próxima fecha son su
    // detalle (RECIBIDO DE, CLAVE DE RASTREO…), sin los encabezados/pies de cada hoja.
    const idxPrimerMov = seccion.findIndex((l) => RE_FECHA_INICIO.test(l));
    const esDePagina = crearFiltroPagina(arr, idxPrimerMov > -1 ? ini + 1 + idxPrimerMov : -1);
    const registros = [];
    let cur = null;
    let ultimo = null; // último movimiento cerrado (recibe las líneas de detalle)
    for (const linea of seccion) {
      if (RE_FECHA_INICIO.test(linea)) {
        ultimo = null;
        if (montosEnLinea(linea).length >= 2) {
          ultimo = { texto: linea, extras: [] }; // movimiento corto: fecha + importe + saldo en una línea
          registros.push(ultimo);
          cur = null;
        } else {
          cur = linea; // inicia (o reinicia por bloque duplicado) un movimiento multi-línea
        }
      } else if (cur != null) {
        cur += " " + linea;
        if (montosEnLinea(cur).length >= 2) {
          ultimo = { texto: cur, extras: [] }; // se cerró al aparecer importe + saldo
          registros.push(ultimo);
          cur = null;
        }
      } else if (ultimo && !esDePagina(linea)) {
        ultimo.extras.push(linea);
      }
    }

    const movimientos = [];
    for (const { texto, extras } of registros) {
      const mFecha = texto.match(RE_FECHA_INICIO);
      if (!mFecha) continue;

      const ms = montosEnLinea(texto);
      if (ms.length < 2) continue;

      const fecha = fechaDesdePartes(mFecha[1], mFecha[2], mFecha[3]);
      // Importe = PRIMER monto, saldo = ÚLTIMO. Robusto al renglón duplicado que
      // emite pdf.js en algunas filas: [importe, importe, saldo, saldo].
      const saldo = ms[ms.length - 1];
      const monto = Math.abs(ms[0]);

      // Tipo por variación del saldo (depósito = cargo, retiro = abono).
      let tipo = null;
      if (prevSaldo != null) {
        tipo = round2(saldo - prevSaldo) >= 0 ? "cargo" : "abono";
      }
      prevSaldo = saldo;

      // Concepto: texto entre la fecha y el primer importe (la descripción duplicada
      // no afecta el empate, que es por monto + tipo + fecha).
      const idxMonto = texto.search(RE_PRIMER_MONTO);
      const concepto = limpiarConcepto(
        texto.slice(mFecha[0].length, idxMonto > -1 ? idxMonto : undefined)
      );

      movimientos.push({
        fecha,
        monto: round2(monto),
        tipo,
        concepto,
        detalle: armarDetalle([concepto, ...extras]),
        saldo,
        raw: texto,
        rawExtra: extras.join(" "),
      });
    }

    return movimientos;
  },
};

export default santander;
